import { useEffect, useState } from 'react'
import { useSessao } from './lib/sessao'
import { destinoDe, ir } from './lib/navegacao'
import { BarraLateral } from './componentes/BarraLateral'
import { Cabecalho } from './componentes/Cabecalho'
import { FaixaAssinatura } from './componentes/FaixaAssinatura'
import { NovaUnidade } from './componentes/NovaUnidade'
import { Busca } from './componentes/Busca'
import { Topo, Vazio } from './componentes/ui'
import { Entrar } from './telas/Entrar'
import { Agenda } from './telas/Agenda'
import { MeuPainel } from './telas/MeuPainel'
import { Relatorios } from './telas/Relatorios'
import { Equipe } from './telas/Equipe'
import { Pacientes } from './telas/Pacientes'
import { Identidade } from './telas/Identidade'
import { Salas } from './telas/Salas'
import { Catalogo } from './telas/Catalogo'
import { Horarios } from './telas/Horarios'
import { Financeiro } from './telas/Financeiro'
import { Caixa } from './telas/Caixa'
import { ContaSeguranca } from './telas/ContaSeguranca'
import { Modelos } from './telas/Modelos'
import { ReguaWa } from './telas/ReguaWa'
import { Inicio } from './telas/Inicio'
import { Fila } from './telas/Fila'
import { Fundador } from './telas/Fundador'
import { AceitarConvite } from './telas/AceitarConvite'
import { Recuperar } from './telas/Recuperar'
import { NovaSenha } from './telas/NovaSenha'
import { Confirmar } from './telas/Confirmar'
import { Agendar } from './telas/Agendar'
import { Home } from './telas/Home'
import { CriarConta } from './telas/CriarConta'
import { Admin } from './telas/Admin'
import { Marca } from './componentes/Marca'
import './App.css'

/**
 * O casco do app.
 *
 * ─── POR QUE DEIXOU DE SER ROTEAMENTO POR ESTADO ──────────────────
 *
 * Era `useState` com seis telas, e o comentário antigo dizia que uma
 * biblioteca de rotas "resolveria um problema que ainda não existe".
 * O problema passou a existir de três formas ao mesmo tempo:
 *
 *  · A API manda `/confirmar?t=` e `/nova-senha?t=` por e-mail, e sem
 *    caminho esses links caíam na home. Verificar e-mail e recuperar
 *    senha não funcionavam para NENHUM cliente.
 *  · Voltar no navegador saía do app inteiro.
 *  · "Manda o link dessa tela" não tinha resposta.
 *
 * Continua sem biblioteca: `pathname` + `popstate` resolvem 13 destinos
 * planos. Uma dependência aqui só se ganhar rota aninhada de verdade.
 */

/** Onde a pessoa está antes de ter sessão. */
type Publica = 'home' | 'criar' | 'entrar'

export function App() {
  const { estado, sair, escolherClinica, pode } = useSessao()

  /* O caminho é a fonte da verdade da navegação. `popstate` cobre tanto
     o botão de voltar quanto o `ir()` do módulo de navegação, que
     dispara o mesmo evento de propósito — um caminho só. */
  const [caminho, setCaminho] = useState(() => window.location.pathname)
  useEffect(() => {
    const ao = () => setCaminho(window.location.pathname)
    window.addEventListener('popstate', ao)
    return () => window.removeEventListener('popstate', ao)
  }, [])

  const [publica, setPublica] = useState<Publica>('home')
  const [buscando, setBuscando] = useState(false)
  const [abrindoUnidade, setAbrindoUnidade] = useState(false)

  /* ⌘K no Mac, Ctrl+K no resto. `preventDefault` porque o Ctrl+K do
     navegador foca a barra de endereço — e a pessoa esperava a busca
     do sistema. */
  useEffect(() => {
    const ao = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault(); setBuscando((b) => !b)
      }
    }
    window.addEventListener('keydown', ao)
    return () => window.removeEventListener('keydown', ao)
  }, [])
  const [planoEscolhido, setPlanoEscolhido] = useState<string | null>(null)

  /**
   * O padrão é CLARO, não "o que o sistema operacional preferir".
   *
   * `docs/pesquisa/design.md` decidiu isso com a evidência na mão: o
   * benefício do modo escuro é condicional (ajuda em sala escura, pode
   * cansar mais em sala clara, e quem tem astigmatismo relata halation),
   * então "light é o default, dark é escolha explícita, persistida POR
   * USUÁRIO".
   */
  const [tema, setTema] = useState<'sistema' | 'claro' | 'escuro'>(
    () => (localStorage.getItem('clinuma:tema') as 'claro' | 'escuro') ?? 'claro',
  )

  useEffect(() => {
    const raiz = document.documentElement
    if (tema === 'sistema') {
      raiz.removeAttribute('data-tema')
      localStorage.removeItem('clinuma:tema')
    } else {
      raiz.setAttribute('data-tema', tema)
      localStorage.setItem('clinuma:tema', tema)
    }
  }, [tema])

  /*
   * Roteamento por HOST, antes de tudo.
   *
   * `admin.` é outro produto: outro cookie, outra sessão, outra cara.
   * Um XSS no app da clínica não alcança nada de lá, e é por isso que
   * são hosts separados em vez de uma rota /admin dentro do app.
   */
  if (window.location.hostname.startsWith('admin.')) return <Admin />

  const busca = new URLSearchParams(window.location.search)
  const token = busca.get('t')

  /* ── caminhos públicos, antes do gate de sessão ──────────────────
     Todos chegam por link de e-mail ou por link colado. Quem tem o
     link não precisa (nem deve) estar logado. */

  if (caminho === '/fundador' && token) return <Fundador token={token} />

  if (caminho === '/convite' && token) {
    return <AceitarConvite token={token} aoEntrar={() => { window.location.href = '/' }} />
  }

  /* As três telas do ciclo de conta. Existiam como rota de API desde
     sempre e como tela nunca — o `href="/recuperar"` do login apontava
     para lugar nenhum e o `/confirmar?t=` do e-mail caía na home. */
  if (caminho === '/recuperar') {
    return <Recuperar emailInicial={busca.get('email') ?? ''}
                      aoVoltar={() => ir('/')} />
  }
  if (caminho === '/nova-senha' && token) {
    return <NovaSenha token={token} aoEntrar={() => ir('/')} />
  }
  if (caminho === '/confirmar' && token) {
    return <Confirmar token={token} aoEntrar={() => ir('/')} />
  }

  /* Caminho de verdade para criar conta: é para cá que o botão do site
     público aponta quando salta do apex para o subdomínio. Sem ele o
     salto cairia no login e a pessoa teria de achar "criar conta" de
     novo, já do outro lado. */
  if (caminho === '/criar-conta' && estado.fase !== 'dentro') {
    return (
      <CriarConta
        {...(planoEscolhido ? { plano: planoEscolhido } : {})}
        aoEntrar={() => { ir('/'); setPublica('entrar') }}
        aoVoltar={() => { ir('/'); setPublica('entrar') }} />
    )
  }

  // Agendamento público: /agendar/<slug-da-clinica>. Sem sessão, sem
  // login, e sem passar por nada do app — é outra audiência.
  const agendar = caminho.match(/^\/agendar\/([a-z0-9-]{3,40})$/)
  if (agendar) return <Agendar slug={agendar[1]!} />

  /*
   * ── APEX × SUBDOMÍNIO ───────────────────────────────────────────
   *
   * O site público vive no apex (`clinuma.com.br`); o sistema vive em
   * `app.`. Isso NÃO é organização — é imposição do cookie.
   *
   * O cookie de sessão usa o prefixo `__Host-`, que é **host-only** por
   * definição: um cookie posto em `clinuma.com.br` NÃO é enviado para
   * `app.clinuma.com.br`, e vice-versa. Logo, entrar no apex e depois
   * mandar a pessoa para `app.` PERDE a sessão — ela chegaria do outro
   * lado deslogada, sem entender por quê.
   *
   * Por isso "Entrar" e "Criar conta" no apex são NAVEGAÇÃO DE VERDADE
   * para o subdomínio, não troca de estado. O login acontece onde o
   * cookie vai valer.
   *
   * Em desenvolvimento (`localhost`) não existe subdomínio, então o
   * fluxo por estado continua — senão não dá para testar nada aqui.
   */
  const host = window.location.hostname
  const noApex = !host.startsWith('app.')
  const temSubdominio = host.includes('.') && !host.endsWith('.localhost')
                        && !/^\d+\.\d+\.\d+\.\d+$/.test(host)
  /* `location.host` e não `hostname`: o primeiro inclui a porta. Com
     `hostname` o salto perde o `:8443` de qualquer ambiente que não
     esteja em 443 e cai em conexão recusada — em produção passaria
     despercebido e quebraria todo teste local. */
  const paraOApp = (caminhoDestino = '/') => {
    window.location.href = `${window.location.protocol}//app.${window.location.host}${caminhoDestino}`
  }

  if (estado.fase === 'carregando') {
    return (
      <div className="carregando">
        <Marca tamanho={34} />
        <p className="ajuda">Carregando…</p>
      </div>
    )
  }

  /* ── O APEX É O SITE, SEMPRE ─────────────────────────────────────
   *
   * Não é "o site quando você não está logado": é o site, ponto. Quem
   * chega em `clinuma.com.br` vê a página de vendas mesmo tendo sessão
   * aberta — porque o sistema mora em `app.`, e essa separação é o que
   * o cookie `__Host-` (host-only) já impõe de qualquer forma.
   *
   * ⚠️ Sem esta guarda o apex renderizava o APP para quem tivesse um
   * cookie antigo dali — de quando o login ainda acontecia no apex. O
   * efeito era o painel no lugar da home, e ninguém entendia por quê,
   * porque em janela anônima o site aparecia certo.
   *
   * Caminho que não seja `/` no apex é link de sistema colado no lugar
   * errado: manda para o subdomínio, preservando o caminho. */
  if (noApex && temSubdominio) {
    if (caminho !== '/') { paraOApp(caminho + window.location.search); return null }
    return (
      <Home aoEntrar={() => paraOApp('/')}
            aoCriarConta={(pl) => { setPlanoEscolhido(pl ?? null); paraOApp('/criar-conta') }} />
    )
  }

  /* ── sem sessão ─────────────────────────────────────────────────
     Daqui para baixo é `app.` (ou `localhost`, em desenvolvimento). A
     home pública só aparece no fluxo por estado do dev — em produção
     ela já foi tratada acima. */
  if (estado.fase === 'fora') {
    if (publica === 'criar') {
      return (
        <CriarConta
          {...(planoEscolhido ? { plano: planoEscolhido } : {})}
          aoEntrar={() => setPublica('entrar')}
          aoVoltar={() => setPublica(noApex ? 'home' : 'entrar')} />
      )
    }
    if (noApex && publica === 'home') {
      return (
        <Home aoEntrar={() => (temSubdominio ? paraOApp('/') : setPublica('entrar'))}
              aoCriarConta={(pl) => {
                setPlanoEscolhido(pl ?? null)
                if (temSubdominio) paraOApp('/criar-conta')
                else setPublica('criar')
              }} />
      )
    }
    return <Entrar aoCriarConta={() => setPublica('criar')} />
  }

  const { ctx } = estado

  // Usuário em mais de uma clínica e nenhuma escolhida ainda.
  if (!ctx.tenantId) {
    return (
      <main className="escolher">
        <div className="escolher__caixa cartao">
          <Marca tamanho={30} />
          <h1>Em qual clínica você vai trabalhar?</h1>
          <p className="ajuda">Dá para trocar depois, sem sair da sessão.</p>
          <ul className="escolher__lista">
            {ctx.clinicas.map((c) => (
              <li key={c.tenantId}>
                <button className="escolher__item"
                        onClick={() => void escolherClinica(c.tenantId)}>
                  <span className="escolher__nome">{c.nome}</span>
                  <span className="selo selo--neutro">{c.papel}</span>
                </button>
              </li>
            ))}
          </ul>
          <button className="botao botao--fantasma" onClick={() => void sair()}>Sair</button>
        </div>
      </main>
    )
  }

  /* Caminho que não existe (ou que o escopo esconde) cai no Início.
     Não é 404: dentro do app, um destino invisível é indistinguível de
     um destino inexistente, e mandar a pessoa para casa é mais honesto
     que uma tela de erro que ela não pode resolver. */
  /* `/ajustes` foi destrinchado em cinco destinos e deixou de existir.
     Quem tem o favorito velho — ou o link de um e-mail antigo — é levado
     à Identidade, que é o começo do que aquela tela fazia. Mandar para
     "essa tela não existe" seria tecnicamente correto e inútil. */
  if (caminho === '/ajustes') { ir('/identidade'); return null }

  const destino = destinoDe(caminho, pode)

  return (
    <div className="app">
      <a className="pular" href="#conteudo">Pular para o conteúdo</a>

      <BarraLateral atual={destino} pode={pode} />

      <Cabecalho
        clinicas={ctx.clinicas}
        tenantId={ctx.tenantId}
        papel={ctx.papel}
        usuario={ctx.nome}
        temaEscuro={tema === 'escuro'}
        aoTrocarClinica={(t) => void escolherClinica(t)}
        aoNovaUnidade={() => setAbrindoUnidade(true)}
        aoBuscar={() => setBuscando(true)}
        aoTrocarTema={() => setTema(tema === 'escuro' ? 'claro' : 'escuro')}
        aoSair={() => void sair()}
      />

      {abrindoUnidade && (
        <NovaUnidade
          aoFechar={() => setAbrindoUnidade(false)}
          aoCriar={async (tenantId) => {
            setAbrindoUnidade(false)
            /* Salta para a unidade recém-criada: quem acabou de abri-la
               vai montá-la agora, e obrigar a trocar no seletor logo
               depois de criar é um passo que não ensina nada. */
            await escolherClinica(tenantId)
            ir('/identidade')
          }}
        />
      )}

      {buscando && <Busca pode={pode} aoFechar={() => setBuscando(false)} />}

      <main className="app__conteudo" id="conteudo">
        <FaixaAssinatura />
        {(() => {
          switch (destino?.caminho) {
            case '/agenda':       return <Agenda />
            case '/fila':         return <Fila />
            case '/meu-dia':      return <MeuPainel />
            case '/pacientes':    return <Pacientes />
            case '/recebimentos': return <Financeiro />
            case '/caixa':        return <Caixa podeEditar={pode('financeiro:write')} foco="caixa" />
            case '/repasse':      return <Caixa podeEditar={pode('financeiro:write')} foco="repasse" />
            case '/relatorios':   return <Relatorios />
            case '/equipe':       return <Equipe />
            case '/prontuario':   return <Modelos podeEditar={pode('config:write')} />
            case '/whatsapp':     return <ReguaWa podeEditar={pode('config:write')} />
            case '/trilha':       return <ContaSeguranca foco="trilha" />
            case '/conta':        return <ContaSeguranca foco="conta" />
            case '/identidade':   return <Identidade />
            case '/salas':        return <Salas />
            case '/catalogo':     return <Catalogo />
            case '/horarios':     return <Horarios />
            /* `destino` é nulo em dois casos diferentes, e eles não
               merecem a mesma resposta:

                · caminho `/` — é o Início mesmo;
                · caminho que não existe (favorito velho, link
                  cortado, erro de digitação) — que ANTES também caía
                  no Início, calado. Quem clicava via "a tela errada
                  abriu" e concluía que a navegação estava quebrada.

               Um destino que existe mas o escopo não permite não passa
               por aqui: `destinoDe` já o filtra antes. */
            default:
              return caminho === '/' ? <Inicio /> : (
                <>
                  <Topo titulo="Essa tela não existe"
                        subtitulo="O endereço aberto não corresponde a nada do sistema." />
                  <Vazio
                    mensagem={`Nada responde por "${caminho}".`}
                    sugestao="Costuma ser um link antigo ou cortado pela metade. Use a barra ao lado, ou ⌘K para procurar pelo nome."
                    acao={<button className="botao" onClick={() => ir('/')}>Ir para o Início</button>}
                  />
                </>
              )
          }
        })()}
      </main>
    </div>
  )
}
